import { cn } from "@/lib/formatters";

export type SortKey = "recent" | "clicks" | "impressions" | "position";

export interface BlogFilterState {
  category: string; // "all" | actual category
  sort: SortKey;
  search: string;
  onlyWithPerf: boolean;
}

interface BlogFiltersProps {
  value: BlogFilterState;
  onChange: (next: BlogFilterState) => void;
  categories: string[]; // includes "all" prepended
  onRefresh: () => void;
  refreshing: boolean;
}

export function BlogFilters({ value, onChange, categories, onRefresh, refreshing }: BlogFiltersProps) {
  const cats = ["all", ...categories.filter((c) => c !== "all")];

  return (
    <div className="surface p-3 flex flex-wrap items-center gap-3 text-sm">
      <div className="flex items-center gap-2 flex-1 min-w-[200px]">
        <span className="path-chip whitespace-nowrap">search</span>
        <input
          type="text"
          value={value.search}
          onChange={(e) => onChange({ ...value, search: e.target.value })}
          placeholder="title, slug, keyword…"
          className="w-full px-2 py-1 bg-bg/60 border border-border rounded-sm text-text font-mono text-xs focus:outline-none focus:border-primary/50"
        />
      </div>

      <div className="flex items-center gap-2">
        <span className="path-chip">cat</span>
        <div className="flex items-center gap-1 flex-wrap">
          {cats.map((c) => {
            const active = value.category === c;
            return (
              <button
                key={c}
                type="button"
                onClick={() => onChange({ ...value, category: c })}
                className={cn(
                  "px-2 py-0.5 rounded-sm font-mono text-xs uppercase tracking-wider border transition-all",
                  active
                    ? "border-primary/50 text-primary bg-primary/10 shadow-glow"
                    : "border-border text-muted hover:text-text hover:border-violet/40",
                )}
              >
                {c}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <span className="path-chip">sort</span>
        <select
          value={value.sort}
          onChange={(e) => onChange({ ...value, sort: e.target.value as SortKey })}
          className="px-2 py-1 bg-bg/60 border border-border rounded-sm text-text font-mono text-xs focus:outline-none focus:border-primary/50"
        >
          <option value="recent">recent</option>
          <option value="clicks">clicks</option>
          <option value="impressions">impressions</option>
          <option value="position">position</option>
        </select>
      </div>

      <label className="flex items-center gap-2 text-muted font-mono text-xs cursor-pointer">
        <input
          type="checkbox"
          checked={value.onlyWithPerf}
          onChange={(e) => onChange({ ...value, onlyWithPerf: e.target.checked })}
          className="accent-primary"
        />
        with gsc data only
      </label>

      <button
        type="button"
        onClick={onRefresh}
        disabled={refreshing}
        className="ml-auto px-3 py-1 text-xs font-mono border border-border rounded-sm text-muted hover:text-text hover:border-primary/40 transition-colors disabled:opacity-50"
      >
        {refreshing ? "refreshing…" : "$ refresh"}
      </button>
    </div>
  );
}
